import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import api from 'utils/api';
import Icon from '../../components/AppIcon';
import { toast } from 'react-hot-toast';

const AIManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');

  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({
    systemPrompt: '',
    temperature: 0.1,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, configRes] = await Promise.all([
        api.get('/api/ai/stats'),
        api.get('/api/ai/config'),
      ]);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await api.put('/api/ai/config', config);
      toast.success('تنظیمات هوش مصنوعی ذخیره شد');
    } catch (err) {
      toast.error('خطا در ذخیره');
    } finally {
      setLoading(false);
    }
  };

  // فرمت کردن پول به دلار
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="lg:mr-60 pt-10 p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Icon name="Bot" className="text-primary" />
            مدیریت هوش مصنوعی (سینوهه)
          </h1>
          <div className="flex bg-muted p-1 rounded-lg">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === 'stats'
                  ? 'bg-white shadow text-primary'
                  : 'text-muted-foreground'
              }`}
              onClick={() => setActiveTab('stats')}>
              آمار
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === 'config'
                  ? 'bg-white shadow text-primary'
                  : 'text-muted-foreground'
              }`}
              onClick={() => setActiveTab('config')}>
              تنظیمات و پرامپت
            </button>
          </div>
        </div>

        {/* --- TAB: STATS --- */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Card 1: Requests */}
              <div className="bg-card border border-border p-4 rounded-xl">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Icon name="MessageSquare" size={18} />
                  <span className="text-sm">کل درخواست‌ها</span>
                </div>
                <div className="text-2xl font-bold">
                  {stats.summary.requests.toLocaleString()}
                </div>
              </div>

              {/* Card 2: Tokens */}
              <div className="bg-card border border-border p-4 rounded-xl">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Icon name="Cpu" size={18} />
                  <span className="text-sm">توکن‌های مصرفی</span>
                </div>
                <div className="text-2xl font-bold">
                  {stats.summary.tokens.totalAll.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 flex justify-between">
                  <span>
                    ورودی: {stats.summary.tokens.totalPrompt.toLocaleString()}
                  </span>
                  <span>
                    خروجی:{' '}
                    {stats.summary.tokens.totalCompletion.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Card 3: Cost (USD)
              <div className="bg-card border border-green-200 bg-green-50/50 p-4 rounded-xl">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <Icon name="DollarSign" size={18} />
                  <span className="text-sm font-bold">هزینه کل (تخمینی)</span>
                </div>
                <div className="text-3xl font-bold text-green-700 dir-ltr text-left">
                  {formatCurrency(stats.summary.cost)}
                </div>
                <div className="text-[10px] text-green-600 mt-1 opacity-80">
                  دلار آمریکا (USD)
                </div>
              </div>*/}

              {/* Card 4: Status */}
              <div className="bg-card border border-border p-4 rounded-xl flex flex-col justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Activity" size={18} />
                  <span className="text-sm">وضعیت سرویس</span>
                </div>
                <div
                  className={`text-xl font-bold flex items-center gap-2 ${
                    config.isActive ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {config.isActive ? (
                    <>
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>{' '}
                      فعال
                    </>
                  ) : (
                    <>
                      <span className="h-3 w-3 rounded-full bg-red-500"></span>{' '}
                      غیرفعال
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Logs Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-border bg-muted/30 font-medium flex justify-between items-center">
                <span>آخرین گفتگوها</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={fetchData}>
                  رفرش
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="p-3 text-right font-medium">زمان</th>
                      <th className="p-3 text-right font-medium">کاربر</th>
                      <th className="p-3 text-right font-medium w-1/3">
                        سوال کاربر
                      </th>
                      <th className="p-3 text-right font-medium w-1/3">
                        پاسخ AI
                      </th>
                      <th className="p-3 text-center font-medium">توکن کل</th>
                      <th className="p-3 text-center font-medium">زمان (ms)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {stats.logs.map((log) => (
                      <tr
                        key={log._id}
                        className="hover:bg-muted/10 transition-colors">
                        <td className="p-3 dir-ltr text-right text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleString('fa-IR')}
                        </td>
                        {/* ✅ نمایش نام و شماره */}
                        <td className="p-3 text-xs">
                          <div className="font-bold text-foreground">
                            {log.clientName}
                          </div>
                          <div className="text-muted-foreground dir-ltr text-right font-mono">
                            {log.clientPhone}
                          </div>
                        </td>

                        <td className="p-3">
                          <div className="line-clamp-2" title={log.userMessage}>
                            {log.userMessage}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          <div className="line-clamp-2" title={log.aiResponse}>
                            {log.aiResponse}
                          </div>
                        </td>
                        <td className="p-3 text-center font-mono text-xs">
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                            {log.totalTokens}
                          </span>
                        </td>
                        <td className="p-3 text-center font-mono text-xs text-muted-foreground">
                          {log.latency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* --- TAB: CONFIG --- */}
        {activeTab === 'config' && (
          <div className="bg-card border border-border rounded-xl p-6 space-y-6 shadow-sm animate-fade-in">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-bold text-lg">تنظیمات هسته</h3>
                <p className="text-sm text-muted-foreground">
                  کنترل رفتار مدل Gemma روی Cloudflare AI (Prompt Engineering)
                </p>
              </div>
              <div className="flex items-center gap-3 bg-muted p-2 rounded-lg">
                <span className="text-sm font-medium">وضعیت ربات:</span>
                <button
                  onClick={() =>
                    setConfig({ ...config, isActive: !config.isActive })
                  }
                  className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all shadow-sm ${
                    config.isActive
                      ? 'bg-white text-green-600 border border-green-200'
                      : 'bg-white text-red-600 border border-red-200'
                  }`}>
                  {config.isActive ? 'روشن (Active)' : 'خاموش (Inactive)'}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Icon name="FileText" size={16} />
                System Prompt (منبع دانش)
              </label>
              <textarea
                className="w-full h-96 p-4 border border-border rounded-lg bg-slate-50 font-mono text-sm leading-relaxed focus:ring-2 focus:ring-primary outline-none resize-y"
                value={config.systemPrompt}
                onChange={(e) =>
                  setConfig({ ...config, systemPrompt: e.target.value })
                }
                placeholder="دستورالعمل‌های هوش مصنوعی را اینجا بنویسید..."
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={12} />
                تغییرات این بخش بلافاصله روی تمام کاربران اعمال می‌شود.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              <div>
                <label className="text-sm font-medium block mb-4 flex justify-between">
                  <span>Temperature (خلاقیت)</span>
                  <span className="bg-primary/10 text-primary px-2 rounded">
                    {config.temperature}
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.temperature}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      temperature: parseFloat(e.target.value),
                    })
                  }
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>۰.۰ (دقیق و منطقی)</span>
                  <span>۱.۰ (خلاق و متنوع)</span>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border flex justify-end">
              <Button
                variant="default"
                onClick={handleSaveConfig}
                loading={loading}
                iconName="Save"
                size="lg">
                ذخیره تغییرات و اعمال آنی
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AIManagement;
